import { CommonModule, formatDate } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { EmpresaEtiquetaDadosService } from '@shared/services/etiqueta/empresa-etiqueta-dados.service';
import { NotaFiscalSaidaListStruct } from '@shared/services/structs/repo-dfe/nota-fiscal-saida-list.struct';
import { NotaFiscalSaidaImpressaoListaDialogData } from './nota-fiscal-saida-impressao-lista-dialog-data';

/**
 * Prévia do relatório "Notas Fiscais de Saída", copiada da impressão da Lista do processo de venda:
 * respeita os filtros, não pagina e imprime por uma janela com os estilos da página.
 */
@Component({
  selector: 'app-nota-fiscal-saida-impressao-lista-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    AlertCardComponent,
    LoadingBoxComponent,
    AbntPipe,
  ],
  templateUrl: './nota-fiscal-saida-impressao-lista-dialog.component.html',
  styleUrls: ['./nota-fiscal-saida-impressao-lista-dialog.component.css'],
})
export class NotaFiscalSaidaImpressaoListaDialogComponent implements OnInit, OnDestroy {
  public isCarregando = true;
  public notas: NotaFiscalSaidaListStruct[] = [];
  public totalRegistros = 0;
  /** A coleção passou do teto do servidor e veio cortada. */
  public isTruncado = false;
  public tetoImpressao = 0;
  public logoUrl = '';

  public readonly colunas: string[] = ['numero', 'tipo', 'dataEmissao', 'cliente', 'uf', 'status', 'valor'];
  public readonly emitidoEm = new Date();

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<NotaFiscalSaidaImpressaoListaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NotaFiscalSaidaImpressaoListaDialogData,
    private notaFiscalSaidaService: NotaFiscalSaidaService,
    private empresaEtiquetaDadosService: EmpresaEtiquetaDadosService,
    private alertService: AlertService
  ) {}

  public ngOnInit(): void {
    this.carregar();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregar(): void {
    // Logo em paralelo, pela mesma regra das etiquetas (principal com urlLogo, senão a primeira com url).
    // Sem logo o relatório sai só com o título.
    this.empresaEtiquetaDadosService
      .getValoresEmpresa()
      .then(valores => (this.logoUrl = valores['empresa.logo'] || ''))
      .catch(() => (this.logoUrl = ''));

    this.notaFiscalSaidaService
      .getListaPrint(this.data.filtro)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isCarregando = false;
          if (response.isError) {
            this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
            this.fechar();
            return;
          }
          this.notas = response.list || [];
          this.totalRegistros = response.totalRegistros;
          this.isTruncado = response.isTruncado;
          this.tetoImpressao = response.tetoImpressao;
        },
        error: () => {
          this.isCarregando = false;
          this.alertService.show('Erro', 'Não foi possível montar o relatório para impressão.', AlertType.error);
          this.fechar();
        },
      });
  }

  public numero(nota: NotaFiscalSaidaListStruct): string {
    return nota.numero != null ? String(nota.numero).padStart(6, '0') : '-';
  }

  public cliente(nota: NotaFiscalSaidaListStruct): string {
    return nota.nomeDestinatario || nota.nomeFantasiaDestinatario || '-';
  }

  public status(nota: NotaFiscalSaidaListStruct): string {
    return this.data.listaStatus.find(x => x.id === nota.idStatusNFeSaida)?.nome || '-';
  }

  public get valorTotal(): number {
    return this.notas.reduce((soma, nota) => soma + (nota.valorTotal || 0), 0);
  }

  /**
   * Abre uma janela só com o conteúdo do relatório e manda imprimir. Copia as folhas de estilo da
   * página porque a janela nova não herda nada; folha de outra origem lança ao ser lida, daí o try/catch.
   * O rodapé "Emitido em" / "Página N de M" vem das margens do @page (Chrome/Edge 131+).
   */
  public imprimir(): void {
    const conteudo = document.querySelector('.modal-print-container');
    if (!conteudo) return;

    const janela = window.open('', '', 'height=600,width=800');
    if (!janela) return;

    const estilos = Array.from(document.styleSheets)
      .map(folha => {
        try {
          return Array.from(folha.cssRules)
            .map(regra => regra.cssText)
            .join('');
        } catch {
          return '';
        }
      })
      .join('');
    const emitidoEm = formatDate(this.emitidoEm, 'dd/MM/yyyy HH:mm', 'pt-BR');

    janela.document.write(`
      <html>
        <head>
          <title>Notas Fiscais de Saída</title>
          <base href="/">
          <style>
            ${estilos}

            @page {
              size: A4 portrait;
              margin: 15mm 12mm 18mm 12mm;
              @bottom-left { content: "Emitido em ${emitidoEm}"; font-size: 9px; }
              @bottom-right { content: "Página " counter(page) " de " counter(pages); font-size: 9px; }
            }

            /* A tela é uma viewport travada com rolagem própria; o papel é o oposto. */
            html, body {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              margin: 0;
              font-family: Roboto, "Helvetica Neue", sans-serif;
              color: #000;
              background: #fff;
            }

            .no-print { display: none !important; }

            .modal-print-container {
              display: block !important;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            /* O dark mode não vai para o papel: branco no branco não imprime. */
            .mat-mdc-table { background: #fff !important; }
            .mat-mdc-header-cell {
              color: #000 !important;
              font-weight: bold !important;
              border-bottom: 2px solid #000 !important;
            }
            .mat-mdc-cell { color: #000 !important; border-bottom: 1px solid #ddd !important; }

            /* Cabeçalho da tabela repetido em toda página de um relatório longo. */
            thead { display: table-header-group; }
            tr { break-inside: avoid; }
          </style>
        </head>
        <body>${conteudo.innerHTML}</body>
      </html>
    `);

    janela.document.close();
    janela.focus();

    // O print precisa da janela já com layout (e logo) pronto; sem a folga ele sai em branco.
    setTimeout(() => {
      janela.print();
      janela.close();
    }, 800);
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
